import React, { useEffect, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { FiRefreshCcw } from "react-icons/fi";

const MerchantTopupTable = () => {

    const today = new Date().toISOString().split("T")[0];

    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);

    /* ── Pagination state ── */
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(50);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [isLastPage, setIsLastPage] = useState(false);
    const [goToPage, setGoToPage] = useState("");

    /* Static base URL — query params are built inside fetchHistoryData */
    const { callApi: fetchHistory } = useApi("/admin/cashin/history", "GET");
    const { callApi: reverseEntry } = useApi("/admin/cashin/reverse", "POST");
    const { callApi: editApproved } = useApi("/admin/cashin/edit-approved", "POST");

    const userId = useSelector((state) => state.auth.employeeDetails.userId);
    const adminId = userId;
    const role = useSelector((state) => state.auth.employeeDetails?.role);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [amountInput, setAmountInput] = useState({});
    const [loadingAction, setLoadingAction] = useState(false);
    const [proofPopup, setProofPopup] = useState({ open: false, url: "" });
    const [message, setMessage] = useState({ text: "", type: "" });
    const [confirmPopup, setConfirmPopup] = useState({ open: false, row: null, index: null });
    const [editPopup, setEditPopup] = useState({ open: false, row: null });
    const [editRemarks, setEditRemarks] = useState("");
    const [downloadLoading, setDownloadLoading] = useState(false);

    /* ── Handlers ── */

    const handleFromDate = (value) => {
        setFromDate(value);
    };

    const handleRefresh = () => {
        setPage(0);
        fetchHistoryData(0);
    };

    // const handleRefresh = () => {
    //     setPage(0);
    //     fetchHistoryData(0);
    //     // 🔥 TRIGGER THE OUTER COMPONENT'S BALANCE REFRESH
    //     if (triggerBalanceRefresh) {
    //         triggerBalanceRefresh();
    //     }
    // };


    const handleFetchForPage = (targetPage) => {
        setPage(targetPage);
        fetchHistoryData(targetPage);
    };

    const handleGoToPage = () => {
        const p = parseInt(goToPage, 10);
        if (isNaN(p) || p < 1 || p > totalPages) return;
        handleFetchForPage(p - 1);
        setGoToPage("");
    };

    /* ── Smart pagination page list ── */
    const getPaginationPages = (current, total) => {
        if (total <= 1) return [];
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
        const pages = [];
        const delta = 2;
        let prev = null;
        for (let i = 1; i <= total; i++) {
            if (i === 1 || i === total || (i >= current + 1 - delta && i <= current + 1 + delta)) {
                if (prev !== null && i - prev > 1) pages.push("...");
                pages.push(i);
                prev = i;
            }
        }
        return pages;
    };

    const paginationPages = getPaginationPages(page, totalPages);
    const rangeStart = totalElements === 0 ? 0 : page * size + 1;
    const rangeEnd = Math.min(page * size + data.length, totalElements);

    const handleEdit = async () => {
        try {
            setLoadingAction(true);
            const payload = { requestId: editPopup.row.requestId, adminId, remarks: editRemarks };
            const res = await editApproved(payload);
            setMessage({ text: res?.message || "Updated Successfully", type: "success" });
            fetchHistoryData(page);
            setTimeout(() => {
                setEditPopup({ open: false, row: null });
                setEditRemarks("");
                setMessage({ text: "", type: "" });
            }, 1200);
        } catch (error) {
            setMessage({ text: error?.message || "Update Failed", type: "error" });
            setTimeout(() => {
                setEditPopup({ open: false, row: null });
                setEditRemarks("");
                setMessage({ text: "", type: "" });
            }, 1500);
        } finally {
            setLoadingAction(false);
        }
    };

    useEffect(() => {
        fetchHistoryData(0);
    }, []);

    /* ── Fetch history with server-side pagination ── */
    const fetchHistoryData = async (targetPage = 0) => {
        try {
            setLoading(true);
            const params = [];
            if (fromDate) params.push(`fromDate=${fromDate}`);
            if (toDate) params.push(`toDate=${toDate}`);
            params.push(`page=${targetPage}`);
            params.push(`size=${size}`);
            const query = `?${params.join("&")}`;

            const res = await fetchHistory(null, query);

            if (res && res.content) {
                /* Paginated response */
                setData(res.content);
                setPage(res.number ?? targetPage);
                setTotalPages(res.totalPages ?? 0);
                setTotalElements(res.totalElements ?? 0);
                setIsLastPage(res.last === true);
                const defaultAmounts = {};
                res.content.forEach((r, i) => { defaultAmounts[i] = r.amount; });
                setAmountInput(defaultAmounts);
            } else if (Array.isArray(res)) {
                /* Fallback — non-paginated */
                setData(res);
                setTotalPages(0);
                setTotalElements(res.length);
                const defaultAmounts = {};
                res.forEach((r, i) => { defaultAmounts[i] = r.amount; });
                setAmountInput(defaultAmounts);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    /* ── Reverse entry ── */
    const handleReverse = async () => {
        const { row, index } = confirmPopup;
        try {
            setLoadingAction(true);
            const payload = { requestId: row.requestId, amount: amountInput[index], adminId, remarks: "Transaction reversal" };
            const res = await reverseEntry(payload);
            setMessage({ text: res?.message || "Entry Reversed Successfully", type: "success" });
            fetchHistoryData(page);
            setTimeout(() => {
                setConfirmPopup({ open: false, row: null, index: null });
                setMessage({ text: "", type: "" });
            }, 1000);
        } catch (error) {
            setMessage({ text: error?.message || "Failed to reverse entry", type: "error" });
            setTimeout(() => {
                setConfirmPopup({ open: false, row: null, index: null });
                setMessage({ text: "", type: "" });
            }, 1500);
        } finally {
            setLoadingAction(false);
        }
    };

    /* ── Excel export — fetches all records using totalElements as size ── */
    const downloadExcel = async () => {
        try {
            setDownloadLoading(true);
            const exportSize = totalElements > 0 ? totalElements : size;
            const params = [];
            if (fromDate) params.push(`fromDate=${fromDate}`);
            if (toDate) params.push(`toDate=${toDate}`);
            params.push(`page=0`);
            params.push(`size=${exportSize}`);
            const query = `?${params.join("&")}`;

            const res = await fetchHistory(null, query);

            let rows = [];
            if (res && res.content) {
                rows = res.content;
            } else if (Array.isArray(res)) {
                rows = res;
            }

            if (rows.length === 0) return;

            const exportData = rows.map((row) => ({
                UserId: row.bpUserId,
                Shop: row.shopName,
                Role: row.role,
                Address: row.district,
                Name: row.name,
                Mobile: row.mobile,
                Amount: row.amount,
                FeeCharged: row.feeCharged ?? 0,
                GSTCharged: row.gstCharged ?? 0,
                NetCredited: row.netCredited ?? row.amount,
                WaiverType: row.waiverType || "-",
                IsThirdParty: row.isTp ? "Yes" : "No",
                ApprovedDate: formatDateTime(row.transferDate),
                DepositDate: row.paymentDate,
                Mode: row.paymentMode,
                Bank: row.bankName,
                Status: row.status,
                requesterRemarks: row.requesterRemarks,
                adminRemarks: row.adminRemarks,
            }));
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "TopupHistory");
            XLSX.writeFile(workbook, "Merchant_Topup_History.xlsx");
        } catch (error) {
            console.error(error);
        } finally {
            setDownloadLoading(false);
        }
    };

    /* ── Helpers ── */

    const formatDateTime = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString.replace(" ", "T"));
        return date.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
    };

    const numberToWords = (num) => {
        const a = ["", "One ", "Two ", "Three ", "Four ", "Five ", "Six ", "Seven ", "Eight ", "Nine ",
            "Ten ", "Eleven ", "Twelve ", "Thirteen ", "Fourteen ", "Fifteen ", "Sixteen ",
            "Seventeen ", "Eighteen ", "Nineteen "];
        const b = ["", "", "Twenty ", "Thirty ", "Forty ", "Fifty ", "Sixty ", "Seventy ", "Eighty ", "Ninety "];
        const inWords = (n) => {
            if (n < 20) return a[n];
            if (n < 100) return b[Math.floor(n / 10)] + a[n % 10];
            if (n < 1000) return a[Math.floor(n / 100)] + "Hundred " + inWords(n % 100);
            if (n < 100000) return inWords(Math.floor(n / 1000)) + "Thousand " + inWords(n % 1000);
            if (n < 10000000) return inWords(Math.floor(n / 100000)) + "Lakh " + inWords(n % 100000);
            return inWords(Math.floor(n / 10000000)) + "Crore " + inWords(n % 10000000);
        };
        return inWords(num) + "Only";
    };

    const getModeColor = (mode) => {
        if (mode === "CASH") return "text-green-600 font-semibold";
        if (mode === "CDM") return "text-blue-600 font-semibold";
        if (mode === "ONLINE") return "text-indigo-600 font-semibold";
        return "";
    };

    /* ════════════════════════ RENDER ════════════════════════ */

    return (

        <div className="w-full mt-10 mb-10">

            <h2 className="text-xl font-bold text-[#0A2A4F] mb-4">CASH-IN REPORT</h2>

            {/* ── Filter bar ── */}
            <div className="flex flex-wrap items-center gap-3 mb-4 bg-gray-50 p-3 rounded-lg border">

                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">From</span>
                    <input type="date" max={today} value={fromDate}
                        onChange={(e) => handleFromDate(e.target.value)}
                        className="border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">To</span>
                    <input type="date" min={fromDate} max={today} value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="border border-gray-300 px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>

                <button onClick={handleRefresh}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition" title="Refresh">
                    <FiRefreshCcw className={loading ? "animate-spin" : ""} size={18} />
                </button>

                <button onClick={downloadExcel} disabled={downloadLoading}
                    className="ml-auto bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-full text-sm font-semibold shadow disabled:opacity-60 disabled:cursor-not-allowed">
                    {downloadLoading ? "Downloading..." : `Download Excel${totalElements > 0 ? ` (${totalElements.toLocaleString("en-IN")})` : ""}`}
                </button>

            </div>

            {/* ── Desktop table ── */}
            <div className="hidden md:block w-full border border-gray-300 bg-white shadow-sm rounded-xl overflow-auto"
                style={{ height: "calc(100vh - 230px)" }}>

                <table className="w-full text-[13px] border-collapse table-fixed">

                    <thead className="bg-[#0A2A4F] text-white sticky top-0 z-10">
                        <tr>
                            <th className="border px-2 py-1.5 text-left w-[170px]">User Details</th>
                            <th className="border px-2 py-1.5 text-left w-[110px]">Amount</th>
                            <th className="border px-2 py-1.5 text-left w-[130px]">Charges & Waiver</th>
                            <th className="border px-2 py-1.5 w-[110px]">Approved</th>
                            <th className="border px-2 py-1.5 w-[100px]">Deposit</th>
                            <th className="border px-2 py-1.5 w-[60px]">Mode</th>
                            <th className="border px-2 py-1.5 w-[140px]">Bank</th>
                            <th className="border px-2 py-1.5 w-[120px]">Requester Remarks</th>
                            <th className="border px-2 py-1.5 w-[180px]">Admin Remarks</th>
                            <th className="border px-2 py-1.5 w-[60px]">Proof</th>
                            <th className="border px-2 py-1.5 w-[90px]">Status</th>
                            {(role === "SUPER_ONBOARDING" || role === "PAYOUT_ACCOUNTING") && (
                                <th className="border px-2 py-1.5 w-[160px]">Enter Amount</th>
                            )}

                            {(role === "SUPER_ONBOARDING" || role === "PAYOUT_ACCOUNTING") && (
                                <th className="border px-2 py-1.5 w-[140px] text-center">Action</th>
                            )}

                        </tr>
                    </thead>

                    <tbody>

                        {loading ? (
                            <tr><td colSpan="13" className="text-center py-6">Loading...</td></tr>
                        ) : data.length > 0 ? (

                            data.map((row, index) => (
                                <tr key={index} className="hover:bg-gray-50 align-top">

                                    <td className="border px-2 py-1.5">
                                        <div className="font-semibold">{row.shopName}</div>
                                        <div>{row.name}</div>
                                        <div className="text-xs text-gray-500">{row.mobile}</div>
                                        <div className="text-xs text-gray-500">{row.role || "-"} | {row.district || "-"} | {row.bpUserId}</div>
                                        <div className="text-xs text-gray-500">ASM :- "-"</div>
                                    </td>

                                    <td className="border px-2 py-1.5 text-left">
                                        <div className="flex flex-col leading-tight">
                                            <span className="font-semibold">₹{row.amount?.toLocaleString()}</span>
                                            <span className="text-[10px] font-semibold tracking-wide">{numberToWords(Math.floor(row.amount || 0))}</span>
                                        </div>
                                    </td>

                                    <td className="border px-2 py-1.5 text-xs">
                                        <div className="flex flex-col gap-0.5 leading-snug">
                                            <div className="flex justify-between gap-1"><span className="text-gray-400">Fee</span><span className="font-medium">₹{row.feeCharged ?? 0}</span></div>
                                            <div className="flex justify-between gap-1"><span className="text-gray-400">GST</span><span className="font-medium">₹{row.gstCharged ?? 0}</span></div>
                                            <div className="flex justify-between gap-1 border-t pt-0.5 mt-0.5"><span className="text-gray-400">Net</span><span className="font-semibold text-green-700">₹{row.netCredited ?? row.amount}</span></div>
                                            <div className="flex justify-between gap-1 mt-0.5">
                                                <span className="text-gray-400">Waiver</span>
                                                <span className={`font-semibold ${row.waiverType === "FULL" ? "text-green-600" : row.waiverType === "PARTIAL" ? "text-yellow-600" : "text-gray-500"}`}>{row.waiverType || "-"}</span>
                                            </div>
                                            <div className="flex justify-between gap-1 mt-0.5">
                                                <span className="text-gray-400">TP</span>
                                                <span className={`font-semibold ${row.isTp ? "text-blue-600" : "text-gray-400"}`}>{row.isTp ? "Yes" : "No"}</span>
                                            </div>
                                        </div>
                                    </td>

                                    <td className="border px-2 py-1.5">{formatDateTime(row.transferDate)}</td>
                                    <td className="border px-2 py-1.5">{row.paymentDate || "-"}</td>

                                    <td className={`border px-2 py-1.5 text-xs text-center ${getModeColor(row.paymentMode)}`}>{row.paymentMode}</td>

                                    <td className="border px-2 py-1.5"><div className="break-words whitespace-normal leading-snug">{row.bankName || "-"}</div></td>
                                    <td className="border px-2 py-1.5"><div className="break-words whitespace-normal leading-snug">{row.requesterRemarks || "-"}</div></td>
                                    <td className="border px-2 py-1.5"><div className="break-words whitespace-normal leading-snug">{row.adminRemarks || "-"}</div></td>

                                    <td className="border px-2 py-1.5 text-center text-xs">
                                        {row.proofUrl ? (
                                            <button onClick={() => setProofPopup({ open: true, url: row.proofUrl })} className="text-blue-600 underline text-xs">View</button>
                                        ) : "-"}
                                    </td>

                                    <td className="border px-2 py-1.5 text-center">
                                        <span className={`text-xs font-semibold ${row.status === "APPROVED" ? "text-green-600" : row.status === "REJECTED" ? "text-red-600" : "text-yellow-600"}`}>
                                            {row.status}
                                        </span>
                                    </td>

                                    {(role === "SUPER_ONBOARDING" || role === "PAYOUT_ACCOUNTING") && (
                                        <td className="border px-2 py-1.5">
                                            {row.status === "APPROVED" ? (
                                                <div className="flex flex-col gap-1">
                                                    <input type="text" inputMode="numeric" value={amountInput[index] || ""}
                                                        className="border px-2 py-1 text-xs rounded w-full text-left focus:ring-1 focus:ring-blue-500 outline-none"
                                                        onChange={(e) => { const value = e.target.value.replace(/\D/g, ""); setAmountInput({ ...amountInput, [index]: value }); }} />
                                                    <div className="text-[10px] font-semibold tracking-wide">{numberToWords(Math.floor(amountInput[index] || 0))}</div>
                                                </div>
                                            ) : "-"}
                                        </td>
                                    )}

                                    {(role === "SUPER_ONBOARDING" || role === "PAYOUT_ACCOUNTING") && (
                                        <td className="border px-2 py-1.5">
                                            <div className="flex justify-center items-center gap-2 w-full">
                                                <button onClick={() => { setEditPopup({ open: true, row }); setEditRemarks(row.adminRemarks || ""); }}
                                                    className="bg-blue-600 hover:bg-blue-700 text-white text-[11px] px-3 py-1.5 rounded whitespace-nowrap">Edit</button>
                                                {row.status === "APPROVED" && (
                                                    <button onClick={() => setConfirmPopup({ open: true, row, index })}
                                                        className="bg-red-600 hover:bg-red-700 text-white text-[11px] px-3 py-1.5 rounded whitespace-nowrap">Reverse</button>
                                                )}

                                            </div>
                                        </td>
                                    )}


                                </tr>

                            ))

                        ) : (
                            <tr><td colSpan="13" className="text-center py-6">No History Found</td></tr>
                        )}

                    </tbody>

                </table>

            </div>

            {/* ── Pagination bar ── */}
            {!loading && totalPages > 0 && (
                <div className="hidden md:flex items-center justify-between gap-3 px-4 py-2.5 mt-2 bg-white border border-gray-300 rounded-xl shadow-sm overflow-x-auto flex-nowrap">

                    {/* LEFT: range + Prev / pages / Next */}
                    <div className="flex items-center gap-1.5 flex-nowrap flex-shrink-0">
                        {totalElements > 0 && (
                            <span className="text-[12px] text-gray-500 whitespace-nowrap mr-1">
                                {rangeStart.toLocaleString("en-IN")}–{rangeEnd.toLocaleString("en-IN")} of {totalElements.toLocaleString("en-IN")}
                            </span>
                        )}
                        <button onClick={() => handleFetchForPage(page - 1)} disabled={page === 0}
                            className="h-8 px-3.5 rounded-lg border border-gray-200 bg-white text-[#0A2A4F] text-[13px] font-medium hover:bg-[#EFF6FF] disabled:opacity-30 disabled:cursor-not-allowed transition flex-shrink-0">
                            ‹ Prev
                        </button>
                        {paginationPages.map((p, i) =>
                            p === "..." ? (
                                <span key={`e${i}`} className="h-8 w-8 flex items-center justify-center text-gray-400 text-sm select-none flex-shrink-0">…</span>
                            ) : (
                                <button key={p} onClick={() => handleFetchForPage(p - 1)}
                                    className={`h-8 min-w-[34px] px-2 rounded-lg text-[13px] font-medium transition border flex-shrink-0 ${
                                        page === p - 1
                                            ? "bg-[#0A2A4F] text-white border-[#0A2A4F]"
                                            : "bg-white text-gray-500 border-gray-200 hover:bg-[#EFF6FF] hover:text-[#0A2A4F]"
                                    }`}>
                                    {p}
                                </button>
                            )
                        )}
                        <button onClick={() => handleFetchForPage(page + 1)} disabled={isLastPage}
                            className="h-8 px-3.5 rounded-lg border border-gray-200 bg-white text-[#0A2A4F] text-[13px] font-medium hover:bg-[#EFF6FF] disabled:opacity-30 disabled:cursor-not-allowed transition flex-shrink-0">
                            Next ›
                        </button>
                    </div>

                    {/* RIGHT: Rows per page + Go to page */}
                    <div className="flex items-center gap-2 flex-nowrap flex-shrink-0">
                        <span className="text-[12px] text-gray-400 whitespace-nowrap">Rows</span>
                        {[50, 100, 200, 500].map((s) => (
                            <button key={s} onClick={() => { setSize(s); setPage(0); fetchHistoryData(0); }}
                                className={`h-8 min-w-[44px] px-3 rounded-full text-[13px] font-medium transition border whitespace-nowrap flex-shrink-0 ${
                                    size === s
                                        ? "bg-[#0A2A4F] text-white border-[#0A2A4F]"
                                        : "bg-white text-gray-500 border-gray-200 hover:bg-[#EFF6FF] hover:text-[#0A2A4F]"
                                }`}>
                                {s}
                            </button>
                        ))}
                        <div className="w-px h-6 bg-gray-200 flex-shrink-0 mx-1" />
                        <span className="text-[12px] text-gray-400 whitespace-nowrap">Go to</span>
                        <input type="number" min={1} max={totalPages} placeholder="Page" value={goToPage}
                            onChange={(e) => setGoToPage(e.target.value)}
                            className="h-8 w-20 px-2.5 border border-gray-200 rounded-lg bg-white text-[13px] text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 outline-none flex-shrink-0" />
                        <button onClick={handleGoToPage}
                            className="h-8 px-4 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] text-white font-medium text-[13px] transition flex-shrink-0">
                            Go
                        </button>
                    </div>

                </div>
            )}

            {/* ── Mobile cards ── */}
            <div className="md:hidden flex flex-col gap-4">

                {loading ? (
                    <div className="text-center py-6">Loading...</div>
                ) : data.length > 0 ? (

                    data.map((row, index) => (
                        <div key={index} className="bg-white border rounded-xl shadow-sm p-4">

                            <div className="mb-2">
                                <div className="font-semibold text-[#0A2A4F]">{row.shopName}</div>
                                <div className="text-sm">{row.name}</div>
                                <div className="text-xs text-gray-500">{row.mobile}</div>
                            </div>

                            <div className="flex justify-between text-sm mb-2">
                                <span className="font-medium">Amount</span>
                                <div className="text-right">
                                    <div className="font-semibold">₹{row.amount?.toLocaleString()}</div>
                                    <div className="text-[10px]">{numberToWords(Math.floor(row.amount || 0))}</div>
                                </div>
                            </div>

                            <div className="bg-gray-50 rounded-lg p-2 mb-2 text-xs">
                                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                                    <div className="flex justify-between"><span className="text-gray-400">Fee</span><span className="font-medium">₹{row.feeCharged ?? 0}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">GST</span><span className="font-medium">₹{row.gstCharged ?? 0}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">Net Credited</span><span className="font-semibold text-green-700">₹{row.netCredited ?? row.amount}</span></div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">Waiver</span>
                                        <span className={`font-semibold ${row.waiverType === "FULL" ? "text-green-600" : row.waiverType === "PARTIAL" ? "text-yellow-600" : "text-gray-500"}`}>{row.waiverType || "-"}</span>
                                    </div>
                                    <div className="flex justify-between col-span-2">
                                        <span className="text-gray-400">Third Party (TP)</span>
                                        <span className={`font-semibold ${row.isTp ? "text-blue-600" : "text-gray-400"}`}>{row.isTp ? "Yes" : "No"}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-between text-sm mb-1"><span>Approved</span><span>{formatDateTime(row.transferDate)}</span></div>
                            <div className="flex justify-between text-sm mb-1"><span>Deposit</span><span>{row.paymentDate || "-"}</span></div>
                            <div className="flex justify-between text-sm mb-1"><span>Mode</span><span className={getModeColor(row.paymentMode)}>{row.paymentMode}</span></div>
                            <div className="flex justify-between text-sm mb-1"><span>Bank</span><span className="text-right">{row.bankName || "-"}</span></div>

                            <div className="flex justify-between text-sm mb-2">
                                <span>Status</span>
                                <span className={`font-semibold ${row.status === "APPROVED" ? "text-green-600" : row.status === "REJECTED" ? "text-red-600" : "text-yellow-600"}`}>{row.status}</span>
                            </div>

                            <div className="text-xs text-gray-600 mb-2">
                                <div><b>Requester:</b> {row.requesterRemarks || "-"}</div>
                                <div><b>Admin:</b> {row.adminRemarks || "-"}</div>
                            </div>

                            {row.proofUrl && (
                                <button onClick={() => setProofPopup({ open: true, url: row.proofUrl })} className="text-blue-600 underline text-xs mb-2">View Proof</button>
                            )}

                            <div className="mt-2 flex flex-col gap-2">
                                <button onClick={() => { setEditPopup({ open: true, row }); setEditRemarks(row.adminRemarks || ""); }}
                                    className="bg-blue-600 text-white py-1.5 rounded text-sm">Edit</button>
                                {row.status === "APPROVED" && (
                                    <>
                                        <input type="text" inputMode="numeric" value={amountInput[index] || ""}
                                            className="border px-2 py-1 text-sm rounded w-full"
                                            onChange={(e) => { const value = e.target.value.replace(/\D/g, ""); setAmountInput({ ...amountInput, [index]: value }); }} />
                                        <div className="text-[10px]">{numberToWords(Math.floor(amountInput[index] || 0))}</div>
                                        <button onClick={() => setConfirmPopup({ open: true, row, index })} className="bg-red-600 text-white py-1.5 rounded text-sm">Reverse</button>
                                    </>
                                )}
                            </div>

                        </div>
                    ))

                ) : (
                    <div className="text-center py-6">No History Found</div>
                )}

                {/* Mobile pagination */}
                {!loading && totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 py-3 flex-wrap">
                        <button onClick={() => handleFetchForPage(page - 1)} disabled={page === 0}
                            className="px-3 py-1.5 rounded-lg border text-[13px] bg-white text-[#0A2A4F] disabled:opacity-30">‹ Prev</button>
                        <span className="text-[13px] text-gray-500">Page {page + 1} of {totalPages}</span>
                        <button onClick={() => handleFetchForPage(page + 1)} disabled={isLastPage}
                            className="px-3 py-1.5 rounded-lg border text-[13px] bg-white text-[#0A2A4F] disabled:opacity-30">Next ›</button>
                    </div>
                )}

            </div>

            {/* ── Confirm reverse popup ── */}
            {confirmPopup.open && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-[340px]">
                        {!message.text && !loadingAction && (
                            <h3 className="text-lg font-semibold mb-4 text-gray-800 text-center">Are you sure you want to reverse this entry?</h3>
                        )}
                        {loadingAction && (
                            <div className="flex flex-col items-center py-4">
                                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                <p className="mt-3 text-sm text-gray-600">Processing...</p>
                            </div>
                        )}
                        {message.text && !loadingAction && (
                            <div className="text-center text-sm font-semibold py-3">{message.text}</div>
                        )}
                        {!loadingAction && !message.text && (
                            <div className="flex justify-end gap-3 mt-4">
                                <button onClick={() => setConfirmPopup({ open: false, row: null, index: null })} className="px-3 py-1.5 bg-gray-300 rounded">Cancel</button>
                                <button onClick={handleReverse} className="px-3 py-1.5 bg-red-600 text-white rounded">OK</button>
                            </div>
                        )}
                    </div>

                </div>

            )}

            {/* ── Proof popup ── */}
            {proofPopup.open && (

                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="relative bg-white rounded-lg shadow-xl w-[90vw] h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center border-b px-4 py-3">
                            <h3 className="font-semibold text-gray-800 text-lg">Payment Proof</h3>
                            <button onClick={() => setProofPopup({ open: false, url: "" })} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm">Close</button>
                        </div>
                        <div className="flex-1 flex items-center justify-center overflow-auto p-4">
                            <img src={proofPopup.url} alt="proof" className="max-h-full max-w-full object-contain rounded" />
                        </div>

                    </div>

                </div>

            )}

            {/* ── Edit remarks popup ── */}
            {editPopup.open && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-[340px]">
                        {!loadingAction && !message.text && (
                            <>
                                <h3 className="text-lg font-semibold mb-4 text-center">Edit Remarks</h3>
                                <textarea value={editRemarks} onChange={(e) => setEditRemarks(e.target.value)}
                                    className="w-full border rounded p-2 text-sm" rows={3} placeholder="Enter remarks" />
                            </>
                        )}
                        {loadingAction && (
                            <div className="flex flex-col items-center py-4">
                                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                <p className="mt-3 text-sm text-gray-600">Updating...</p>
                            </div>
                        )}
                        {message.text && !loadingAction && (
                            <div className="text-center text-sm font-semibold py-3">{message.text}</div>
                        )}
                        {!loadingAction && !message.text && (
                            <div className="flex justify-end gap-3 mt-4">
                                <button onClick={() => setEditPopup({ open: false, row: null })} className="px-3 py-1.5 bg-gray-300 rounded">Cancel</button>
                                <button onClick={handleEdit} className="px-3 py-1.5 bg-blue-600 text-white rounded">Save</button>
                            </div>
                        )}
                    </div>

                </div>

            )}

        </div>

    );
};

export default MerchantTopupTable;
